
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import PageMeta from "@/components/seo/PageMeta";
import GammaEmbed from "@/components/GammaEmbed";
import ChecklistDownload from "@/components/checklist/ChecklistDownload";
import ResourceCard from "@/components/insights/ResourceCard";
import { getInsightResources } from "@/services/insightResourceService";
import type { InsightResource } from "@/services/insightResourceService";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookOpen, ArrowRight } from "lucide-react";

const InsightsPage: React.FC = () => {
  const [resources, setResources] = useState<InsightResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);

  const topics = [
    "Oversight Models",
    "Risk & Materiality",
    "Reporting & Metrics",
    "Vendor Risk",
    "Incident Readiness",
    "Regulation & Governance Trends",
    "Templates & Toolkits",
  ];

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const data = await getInsightResources();
        setResources(data);
      } catch (error) {
        console.error("Error fetching resources:", error);
        toast({
          title: "Error",
          description: "Failed to load resources",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  return (
    <Layout>
      <PageMeta
        title="Insights for Directors Governing AI"
        description="Practical guidance for boards and board-facing leaders: oversight models, reporting structures, governance templates, and the questions directors should be asking."
        canonicalUrl="https://boardsight.ai/insights"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-techne-purple/5 via-background to-background py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Insights for Directors Governing AI
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Practical guidance for boards and board‑facing leaders: oversight models, reporting structures, governance templates, and the questions directors should be asking.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <Link to="/quiz">Take the Assessment</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/contact#briefing">Book a Briefing</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Book Section */}
      <section className="py-16 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border border-border rounded-2xl p-8 md:p-12 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-techne-purple/10 rounded-xl">
                  <BookOpen className="w-8 h-8 text-techne-purple" />
                </div>
                <Badge variant="secondary" className="text-sm">Coming Soon</Badge>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Director AI Oversight — The Book
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                A boardroom handbook for governing AI risk with confidence. The book will cover oversight responsibilities, operating models, reporting expectations, incident escalation, and practical templates for directors.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg">
                  <Link to="/contact#book">
                    Get Book Updates
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/blueprint">Learn About Our Programs</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topic Filters */}
      <section className="py-8 border-b border-border">
        <div className="container-custom">
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              variant={selectedTopic === null ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedTopic(null)}
            >
              All Topics
            </Button>
            {topics.map((topic) => (
              <Button
                key={topic}
                variant={selectedTopic === topic ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedTopic(topic)}
              >
                {topic}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-16">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <GammaEmbed />
            <ChecklistDownload />
          </div>

          {resources.length > 0 && (
            <div className="mb-16">
              <h2 className="text-2xl font-bold text-foreground mb-6">Additional Resources</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {resources.map((resource) => (
                  <ResourceCard
                    key={resource.id}
                    title={resource.title}
                    description={resource.description || undefined}
                    imageUrl={resource.image_url || undefined}
                    filePath={resource.file_path}
                    isGated={resource.is_gated}
                    resourceType={resource.resource_type}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Newsletter Signup */}
          <div className="bg-muted/50 p-8 rounded-xl border border-border">
            <h2 className="text-2xl font-bold text-foreground mb-4">Stay Updated</h2>
            <p className="text-muted-foreground mb-4">
              We're preparing additional insights and research papers on board-level AI governance. 
              Subscribe to our newsletter to be notified when new resources become available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <input
                type="email"
                placeholder="Your email address"
                className="px-4 py-2 border border-border rounded-md w-full sm:w-64 bg-background"
              />
              <Button>Subscribe</Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default InsightsPage;
